/**
 * Admin — News Settings
 *
 * Three sections:
 *   1. API Configuration — NewsAPI.ai key, enable/disable, provider settings
 *   2. Master Source List — Curated sources available platform-wide
 *   3. Community Access — Per-community premium toggles
 *
 * English only (admin UI per CLAUDE.md).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getSystemSetting,
  putSystemSetting,
  SETTING_KEYS,
} from '../../api/systemSettings';
import {
  fetchMasterSources,
  addMasterSource,
  updateMasterSource,
  searchSourceCatalog,
  fetchCommunityNewsAccess,
  toggleCommunityNewsAccess,
  NewsSource,
  SourceSearchResult,
  CommunityNewsAccess,
} from '../../api/newsApi';
import styles from './AdminPages.module.css';

/* ------------------------------------------------------------------ */
/*  Default seeded sources                                              */
/* ------------------------------------------------------------------ */

const SEED_SOURCES: Partial<NewsSource>[] = [
  { sourceUri: 'apnews.com', sourceName: 'Associated Press', sourceDomain: 'apnews.com', category: 'general' },
  { sourceUri: 'reuters.com', sourceName: 'Reuters', sourceDomain: 'reuters.com', category: 'general' },
  { sourceUri: 'bbc.co.uk', sourceName: 'BBC News', sourceDomain: 'bbc.co.uk', category: 'general' },
  { sourceUri: 'nytimes.com', sourceName: 'The New York Times', sourceDomain: 'nytimes.com', category: 'general' },
  { sourceUri: 'washingtonpost.com', sourceName: 'The Washington Post', sourceDomain: 'washingtonpost.com', category: 'general' },
  { sourceUri: 'theguardian.com', sourceName: 'The Guardian', sourceDomain: 'theguardian.com', category: 'general' },
  { sourceUri: 'aljazeera.com', sourceName: 'Al Jazeera', sourceDomain: 'aljazeera.com', category: 'general' },
  { sourceUri: 'cnn.com', sourceName: 'CNN', sourceDomain: 'cnn.com', category: 'general' },
  { sourceUri: 'bloomberg.com', sourceName: 'Bloomberg', sourceDomain: 'bloomberg.com', category: 'business' },
  { sourceUri: 'nature.com', sourceName: 'Nature', sourceDomain: 'nature.com', category: 'science' },
];

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export default function AdminNewsSettings() {
  // ── API Config state ─────────────────────────────────────
  const [apiKey, setApiKey] = useState('');
  const [apiEnabled, setApiEnabled] = useState(false);
  const [baseUrl, setBaseUrl] = useState('https://eventregistry.org/api/v1');
  const [defaultLang, setDefaultLang] = useState('eng');
  const [maxArticles, setMaxArticles] = useState('15');
  const [savedApiKey, setSavedApiKey] = useState('');
  const [savedEnabled, setSavedEnabled] = useState(false);
  const [savedBaseUrl, setSavedBaseUrl] = useState('https://eventregistry.org/api/v1');
  const [savedDefaultLang, setSavedDefaultLang] = useState('eng');
  const [savedMaxArticles, setSavedMaxArticles] = useState('15');

  // ── Master Sources state ─────────────────────────────────
  const [sources, setSources] = useState<NewsSource[]>([]);
  const [sourceSearch, setSourceSearch] = useState('');
  const [sourceResults, setSourceResults] = useState<SourceSearchResult[]>([]);
  const [searchingSource, setSearchingSource] = useState(false);

  // ── Community Access state ───────────────────────────────
  const [communities, setCommunities] = useState<CommunityNewsAccess[]>([]);

  // ── General state ────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
  const statusTimer = useRef<ReturnType<typeof setTimeout>>();

  // ── Load all data on mount ───────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Load API settings
        const [key, enabled, url, lang, max] = await Promise.all([
          getSystemSetting(SETTING_KEYS.NEWS_API_KEY),
          getSystemSetting(SETTING_KEYS.NEWS_API_ENABLED),
          getSystemSetting(SETTING_KEYS.NEWS_API_BASE_URL),
          getSystemSetting(SETTING_KEYS.NEWS_DEFAULT_LANGUAGE),
          getSystemSetting(SETTING_KEYS.NEWS_MAX_ARTICLES_PER_FETCH),
        ]);

        if (cancelled) return;

        const keyVal = key ?? '';
        const enabledVal = enabled === 'true';
        const urlVal = url ?? 'https://eventregistry.org/api/v1';
        const langVal = lang ?? 'eng';
        const maxVal = max ?? '15';

        setApiKey(keyVal);
        setSavedApiKey(keyVal);
        setApiEnabled(enabledVal);
        setSavedEnabled(enabledVal);
        setBaseUrl(urlVal);
        setSavedBaseUrl(urlVal);
        setDefaultLang(langVal);
        setSavedDefaultLang(langVal);
        setMaxArticles(maxVal);
        setSavedMaxArticles(maxVal);

        // Load master sources (will 404 gracefully if backend not ready)
        try {
          const src = await fetchMasterSources();
          if (!cancelled) setSources(src);
        } catch {
          // Backend not ready yet — show seeded defaults as placeholder
          if (!cancelled) {
            setSources(SEED_SOURCES.map((s) => ({
              sourceUri: s.sourceUri!,
              sourceName: s.sourceName!,
              sourceDomain: s.sourceDomain,
              provider: 'newsapi_ai',
              category: s.category,
              isEnabled: true,
              isSeeded: true,
            } as NewsSource)));
          }
        }

        // Load community access
        try {
          const comm = await fetchCommunityNewsAccess();
          if (!cancelled) setCommunities(comm);
        } catch {
          // Backend not ready
        }

      } catch {
        // Partial failures handled above
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Flash status ─────────────────────────────────────────
  const flash = useCallback((type: 'success' | 'error', msg: string) => {
    setStatus({ type, msg });
    if (statusTimer.current) clearTimeout(statusTimer.current);
    statusTimer.current = setTimeout(() => setStatus(null), 4000);
  }, []);

  // ── Save API config ──────────────────────────────────────
  const handleSaveApiConfig = useCallback(async () => {
    setSaving('api');
    try {
      await Promise.all([
        putSystemSetting(SETTING_KEYS.NEWS_API_KEY, apiKey),
        putSystemSetting(SETTING_KEYS.NEWS_API_ENABLED, String(apiEnabled)),
        putSystemSetting(SETTING_KEYS.NEWS_API_BASE_URL, baseUrl),
        putSystemSetting(SETTING_KEYS.NEWS_DEFAULT_LANGUAGE, defaultLang),
        putSystemSetting(SETTING_KEYS.NEWS_MAX_ARTICLES_PER_FETCH, maxArticles),
      ]);
      setSavedApiKey(apiKey);
      setSavedEnabled(apiEnabled);
      setSavedBaseUrl(baseUrl);
      setSavedDefaultLang(defaultLang);
      setSavedMaxArticles(maxArticles);
      flash('success', 'API settings saved');
    } catch (err: any) {
      flash('error', `Failed to save: ${err.message || 'Unknown error'}`);
    } finally {
      setSaving(null);
    }
  }, [apiKey, apiEnabled, baseUrl, defaultLang, maxArticles, flash]);

  const apiDirty =
    apiKey !== savedApiKey ||
    apiEnabled !== savedEnabled ||
    baseUrl !== savedBaseUrl ||
    defaultLang !== savedDefaultLang ||
    maxArticles !== savedMaxArticles;

  // ── Source catalog search ────────────────────────────────
  const handleSourceSearch = useCallback(async () => {
    if (!sourceSearch.trim()) return;
    setSearchingSource(true);
    try {
      const results = await searchSourceCatalog(sourceSearch.trim());
      setSourceResults(results);
    } catch {
      // If API not ready, show empty
      setSourceResults([]);
    } finally {
      setSearchingSource(false);
    }
  }, [sourceSearch]);

  // ── Add source from search results ──────────────────────
  const handleAddSource = useCallback(async (result: SourceSearchResult) => {
    const existing = sources.find(
      (s) => s.sourceUri.toUpperCase() === result.uri.toUpperCase()
    );
    if (existing) {
      flash('error', `"${result.title}" is already in the master list`);
      return;
    }
    try {
      const added = await addMasterSource({
        sourceUri: result.uri,
        sourceName: result.title,
        sourceDomain: result.domain,
        country: result.country,
        language: result.language,
        category: result.category,
        isEnabled: true,
      });
      setSources((prev) => [...prev, added]);
      setSourceResults((prev) => prev.filter((r) => r.uri !== result.uri));
      flash('success', `Added "${result.title}"`);
    } catch {
      // If backend not ready, add locally
      const local: NewsSource = {
        sourceUri: result.uri,
        sourceName: result.title,
        sourceDomain: result.domain,
        provider: 'newsapi_ai',
        country: result.country,
        language: result.language,
        category: result.category,
        isEnabled: true,
        isSeeded: false,
      };
      setSources((prev) => [...prev, local]);
      setSourceResults((prev) => prev.filter((r) => r.uri !== result.uri));
      flash('success', `Added "${result.title}" (local — save pending backend)`);
    }
  }, [sources, flash]);

  // ── Toggle source enabled/disabled ──────────────────────
  const handleToggleSource = useCallback(async (sourceUri: string, enabled: boolean) => {
    try {
      await updateMasterSource(sourceUri, { isEnabled: enabled });
    } catch {
      // Continue even if backend not ready
    }
    setSources((prev) =>
      prev.map((s) =>
        s.sourceUri.toUpperCase() === sourceUri.toUpperCase()
          ? { ...s, isEnabled: enabled }
          : s
      )
    );
  }, []);

  // ── Toggle community access ─────────────────────────────
  const handleToggleCommunity = useCallback(async (guid: string, enabled: boolean) => {
    try {
      await toggleCommunityNewsAccess(guid, enabled);
    } catch {
      // Continue even if backend not ready
    }
    setCommunities((prev) =>
      prev.map((c) =>
        c.communityGuid.toUpperCase() === guid.toUpperCase()
          ? { ...c, newsEnabled: enabled }
          : c
      )
    );
  }, []);

  // ── Render ───────────────────────────────────────────────
  if (loading) {
    return (
      <div>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>News Settings</h1>
            <p className={styles.pageSubtitle}>Loading…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>News Settings</h1>
          <p className={styles.pageSubtitle}>
            Configure the news feed system — API access, curated sources, and community access.
          </p>
        </div>
      </div>

      {status && (
        <div className={`${styles.statusBar} ${status.type === 'success' ? styles.status_success : styles.status_error}`}>
          {status.msg}
        </div>
      )}

      {/* ═══ Section 1: API Configuration ═══ */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>API Configuration</h2>
          <button
            className={`${styles.btnPrimary} ${apiDirty ? styles.btnDirty : ''}`}
            disabled={!apiDirty || saving === 'api'}
            onClick={handleSaveApiConfig}
          >
            {saving === 'api' ? 'Saving…' : apiDirty ? 'Save Changes' : 'All Saved'}
          </button>
        </div>
        <p className={styles.sectionHint}>
          NewsAPI.ai credentials and defaults. The API key is stored server-side and never exposed to the browser.
        </p>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={apiEnabled}
              onChange={(e) => setApiEnabled(e.target.checked)}
              style={{ width: 16, height: 16 }}
            />
            News API Enabled
          </label>
          <span style={{ fontSize: 11, color: apiEnabled ? '#166534' : '#991b1b' }}>
            {apiEnabled ? 'Active' : 'Disabled'}
          </span>
        </div>

        <div className={styles.fieldGrid}>
          <label className={styles.fieldLabel}>
            API Key
            <span className={styles.fieldHint}>NewsAPI.ai developer key</span>
            <input
              className={styles.input}
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your NewsAPI.ai API key"
            />
          </label>

          <label className={styles.fieldLabel}>
            Base URL
            <span className={styles.fieldHint}>EventRegistry API base URL</span>
            <input
              className={styles.input}
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://eventregistry.org/api/v1"
            />
          </label>

          <label className={styles.fieldLabel}>
            Default Language
            <span className={styles.fieldHint}>ISO language code (e.g., eng, fra, deu)</span>
            <input
              className={styles.input}
              type="text"
              value={defaultLang}
              onChange={(e) => setDefaultLang(e.target.value)}
              placeholder="eng"
            />
          </label>

          <label className={styles.fieldLabel}>
            Max Articles per Fetch
            <span className={styles.fieldHint}>Default limit per query</span>
            <input
              className={styles.input}
              type="number"
              min={1}
              max={100}
              value={maxArticles}
              onChange={(e) => setMaxArticles(e.target.value)}
              placeholder="15"
            />
          </label>
        </div>
      </div>

      {/* ═══ Section 2: Master Source List ═══ */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Master Source List</h2>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {sources.filter((s) => s.isEnabled).length} enabled / {sources.length} total
          </span>
        </div>
        <p className={styles.sectionHint}>
          Curated news sources available to all communities. Toggle sources on/off or search the NewsAPI.ai catalog to add more.
        </p>

        {/* Source search */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            className={styles.input}
            type="text"
            value={sourceSearch}
            onChange={(e) => setSourceSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSourceSearch()}
            placeholder="Search NewsAPI.ai source catalog (e.g., 'Boston Globe')…"
            style={{ flex: 1 }}
          />
          <button
            className={styles.btnSecondary}
            onClick={handleSourceSearch}
            disabled={searchingSource || !sourceSearch.trim()}
          >
            {searchingSource ? 'Searching…' : 'Search'}
          </button>
        </div>

        {/* Search results */}
        {sourceResults.length > 0 && (
          <div style={{ marginBottom: 16, border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ padding: '8px 12px', background: '#f9fafb', fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase' }}>
              Search Results
            </div>
            {sourceResults.map((r) => (
              <div key={r.uri} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', borderTop: '1px solid #f3f4f6' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}>{r.title}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>
                    {r.domain}{r.country ? ` · ${r.country}` : ''}{r.category ? ` · ${r.category}` : ''}
                  </div>
                </div>
                <button className={styles.btnAdd} onClick={() => handleAddSource(r)}>
                  + Add
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Current sources table */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f9fafb', textAlign: 'start' }}>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Enabled</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Source</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Domain</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Category</th>
                <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Origin</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={s.sourceUri} style={{ borderTop: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={s.isEnabled}
                      onChange={(e) => handleToggleSource(s.sourceUri, e.target.checked)}
                      style={{ width: 14, height: 14, cursor: 'pointer' }}
                    />
                  </td>
                  <td style={{ padding: '6px 12px', fontWeight: 500, color: s.isEnabled ? '#1a1a2e' : '#9ca3af' }}>
                    {s.sourceName}
                  </td>
                  <td style={{ padding: '6px 12px', color: '#6b7280' }}>{s.sourceDomain || '—'}</td>
                  <td style={{ padding: '6px 12px', color: '#6b7280' }}>{s.category || '—'}</td>
                  <td style={{ padding: '6px 12px' }}>
                    {s.isSeeded ? (
                      <span style={{ fontSize: 10, padding: '2px 6px', background: '#dbeafe', color: '#1d4ed8', borderRadius: 3, fontWeight: 600 }}>SEED</span>
                    ) : (
                      <span style={{ fontSize: 10, padding: '2px 6px', background: '#f3f4f6', color: '#6b7280', borderRadius: 3, fontWeight: 600 }}>ADDED</span>
                    )}
                  </td>
                </tr>
              ))}
              {sources.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: 20, textAlign: 'center', color: '#9ca3af', fontStyle: 'italic' }}>
                    No sources configured. Search the catalog to add sources.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══ Section 3: Community Access ═══ */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Community Access</h2>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {communities.filter((c) => c.newsEnabled).length} enabled / {communities.length} total
          </span>
        </div>
        <p className={styles.sectionHint}>
          News API is a premium feature. Enable it per community to control costs. Communities without access can still receive user-submitted and shared content.
        </p>

        {communities.length > 0 ? (
          <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', textAlign: 'start' }}>
                  <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Enabled</th>
                  <th style={{ padding: '8px 12px', fontWeight: 600, fontSize: 11, color: '#6b7280', textTransform: 'uppercase' }}>Community</th>
                </tr>
              </thead>
              <tbody>
                {communities.map((c) => (
                  <tr key={c.communityGuid} style={{ borderTop: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '6px 12px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={c.newsEnabled}
                        onChange={(e) => handleToggleCommunity(c.communityGuid, e.target.checked)}
                        style={{ width: 14, height: 14, cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '6px 12px', fontWeight: 500, color: c.newsEnabled ? '#1a1a2e' : '#9ca3af' }}>
                      {c.communityName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className={styles.emptyMsg}>
            No communities found. Community access toggles will appear once the backend is connected and communities exist.
          </p>
        )}
      </div>

    </div>
  );
}
